import React, { useEffect, useState, useRef } from "react";
import httpClient from "../../_util/api";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, TextField, IconButton,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import ApplicationDetails from "../../components/ApplicationDetails";
import CustomizedSnackbars from "../../shared/Snackbar/SnackBar";
import EditStudentModal from "../../components/EditStudentModal";
import useDebounce from "../../_util/UseDebounce";

const Students = () => {
  const snackbar_Ref = useRef();
  // Always show Edit column (no role gate — route is protected; API enforces on Save)

  const [data, setData] = useState([]);
  const [isViewDetails, setIsViewDetails] = useState(false);
  const [CNICNo, setCNICNo] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalApplications, setTotalApplications] = useState(0); // Added total count state

  const latestRequestRef = useRef(0);
  const getAllApplications = async () => {
    // Only the most recent request may update the table; an older, slower
    // response must not overwrite newer results.
    const requestId = ++latestRequestRef.current;
    try {
      const res = await httpClient.get('/students/getAllStudents', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch.trim()
        }
      });
      if (requestId !== latestRequestRef.current) return;
      if (res.status === 200) {
        setData(res.data.applications);
        setTotalApplications(res.data.pagination.totalApplications || 0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Wait for the user to stop typing before searching, and start from the first page.
  useDebounce(() => {
    setDebouncedSearch(search);
    setPage(0);
  }, [search], 500);

  useEffect(() => {
    getAllApplications();
  }, [page, rowsPerPage, debouncedSearch]);

  if (isViewDetails) {
    return <ApplicationDetails CNICNo={CNICNo} />;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  // Edit student modal: full profile edit, delegated to the shared
  // EditStudentModal (also used from the reviewer/approver "Take Action" screen).
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);

  const openEditModal = (student) => {
    setEditStudent(student);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditStudent(null);
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", padding: 2 }}>
      <CustomizedSnackbars ref={snackbar_Ref} />
      <TextField
        label="Search by Name or Email"
        variant="outlined"
        fullWidth
        value={search}
        onChange={handleSearchChange}
        sx={{ marginBottom: 2 }}
      />
      <TableContainer>
        <Table>
          <TableHead sx={{ backgroundColor: "#4caf50" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell> {/* Row Number */}
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>CNIC</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Phone NO</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Country</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Address</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                key={item._id || item.id}
                // sx={{ cursor: "pointer" }}
                // onClick={() => {
                //   setCNICNo(item.CNICNo);
                //   setIsViewDetails(true);
                // }}
              >
                <TableCell>{page * rowsPerPage + index + 1}</TableCell> {/* Row number */}
                <TableCell>{item.firstName} {item.lastName}</TableCell>
                <TableCell>{item.email}</TableCell>
                <TableCell>{item.CNICNo || "-"}</TableCell>
                <TableCell>{item.phoneNo_Mobile || "-"}</TableCell>
                <TableCell>{item.country}</TableCell>
                <TableCell>{item.address}</TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>
                  <IconButton
                    color="primary"
                    size="small"
                    title="Edit"
                    onClick={() => {
                      if (!item._id) {
                        snackbar_Ref.current?.showMessage("error", "Student id missing — cannot edit", "", "i-chk-circle");
                        return;
                      }
                      openEditModal(item);
                    }}
                    sx={{ border: "1px solid #4caf50" }}
                  >
                    <Edit />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalApplications}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <EditStudentModal
        open={editModalOpen}
        student={editStudent}
        onClose={closeEditModal}
        onSaved={(updated) => {
          setData((prev) =>
            prev.map((s) => (s._id === editStudent?._id ? { ...s, ...updated } : s))
          );
        }}
        onNotify={(type, msg) => snackbar_Ref.current?.showMessage(type, msg, "", "i-chk-circle")}
      />
    </Paper>
  );
};

export default Students;
